import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cancelWarmTransfer,
  getWarmTransfer,
  leaveWarmTransfer,
  mergeWarmTransfer,
} from "../api/warmTransfer";
import {
  agentHeartbeat,
  agentLogin,
  agentLogout,
  agentPause,
  getDialInfo,
  getSipCredentials,
  logoutBeacon,
  type CurrentCall,
} from "../api/agent";
import { listExtensions } from "../api/extensions";
import { ConsentPanel } from "./ConsentPanel";
import {
  CALL_INVALID_TARGET,
  CALL_NOT_CONNECTED,
  Softphone,
  primeAudioPlayback,
  TRANSFER_INVALID_TARGET,
  TRANSFER_NO_CALL,
  type PhoneState,
} from "../services/softphone";
import { AlertTone } from "../services/alertTone";
import { setCallGuardActive } from "../services/callGuard";
import { Dialpad } from "./Dialpad";
import { WarmTransferPanel } from "./WarmTransferPanel";

const HEARTBEAT_MS = 30_000;
// Matches WarmTransferPanel's own POLL_MS. Same query key, so react-query
// dedupes the two subscribers into a single request on this interval.
const WARM_POLL_MS = 2000;
// beads F3: how long a real "go online" connect() attempt is given before
// this side gives up and reports an error, instead of leaving the button
// stuck on "Connecting…" forever. Mirrors connectionTest.ts's own
// Promise.race/ECHO_CALL_TIMEOUT_MS pattern — its own comment documents this
// exact failure live: a network/firewall silently dropping traffic to the
// WSS port leaves SimpleUser.connect()'s promise neither resolving nor
// rejecting. Generous relative to the self-test's 8s since a real login also
// carries agentLogin()/getSipCredentials() round-trips before connect() even
// starts, and a slower-but-real ICE negotiation must not be mistaken for a
// dead network.
const GO_ONLINE_TIMEOUT_MS = 15_000;
const MIC_BLOCKED_ERROR = "Your browser blocked the microphone";
const PHONE_SERVER_ERROR = "Can't reach the phone server";
const TRANSFER_FAILED_ERROR = "Couldn't transfer the call";
const DIAL_FAILED_ERROR = "Couldn't place the call";
const RECENTS_KEY = "agentconsole:recent-transfers";
const RECENTS_MAX = 4;

const STATE_LABEL: Record<PhoneState, string> = {
  offline: "Offline",
  registering: "Connecting",
  idle: "Ready",
  calling: "Calling…",
  ringing: "Incoming call",
  "on-call": "On call",
};

/**
 * One tone per console state. The spine (the colour bar down the card's left
 * edge) is the whole point: an agent's eyes are on the customer, not on the
 * phone widget, so state has to be legible in peripheral vision without
 * reading a word of it. Colours follow the conventions every ACD already
 * trained these agents on — green available, amber paused, grey off.
 */
type Tone = { spine: string; dot: string; label: string };

const TONE: Record<string, Tone> = {
  offline: { spine: "bg-slate-300", dot: "bg-slate-400", label: "text-slate-500" },
  connecting: { spine: "bg-amber-400", dot: "bg-amber-500", label: "text-amber-700" },
  ready: { spine: "bg-emerald-500", dot: "bg-emerald-500", label: "text-emerald-700" },
  break: { spine: "bg-amber-500", dot: "bg-amber-500", label: "text-amber-700" },
  ringing: { spine: "bg-brand", dot: "bg-brand", label: "text-brand-dark" },
  oncall: { spine: "bg-emerald-400", dot: "bg-emerald-400", label: "text-emerald-300" },
};

/** US-formats a 10/11-digit number, passes anything else through untouched. */
function formatPhone(raw: string): string {
  const d = raw.replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return raw;
}

/**
 * The one-line facts under a caller's number, from `CurrentCall.lead`. Each
 * line is only emitted when every part of it is actually known — the
 * backend sends null for "unknown" and this never fills a gap with a guess.
 */
export function leadFactLines(call: CurrentCall): string[] {
  const { lead } = call;
  const out: string[] = [];
  const where = [lead.state, lead.area_code ? `${lead.area_code} area code` : null]
    .filter(Boolean)
    .join(" · ");
  if (where || lead.local_time) {
    out.push([where, lead.local_time ? `${lead.local_time} their time` : null].filter(Boolean).join(" · "));
  }
  if (lead.carrier || lead.line_type) {
    out.push([lead.line_type ? lead.line_type[0].toUpperCase() + lead.line_type.slice(1) : null, lead.carrier]
      .filter(Boolean)
      .join(" · "));
  }
  const attempt = lead.attempt != null
    ? `Attempt ${lead.attempt}${lead.try_allowed != null ? ` of ${lead.try_allowed}` : ""}`
    : null;
  if (lead.list_name || attempt) {
    out.push([lead.list_name ? `List: ${lead.list_name}` : null, attempt].filter(Boolean).join(" · "));
  }
  if (call.email) out.push(call.email);
  return out;
}

/** "Sep 17, 2:03 PM" in the agent's own zone — short enough for a history row. */
function formatHistoryDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** mm:ss, growing to h:mm:ss past the hour. Padded so digits never reflow. */
function formatDuration(total: number): string {
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

// localStorage is the whole store for recent transfer targets: the numbers an
// agent transfers to are a personal, per-browser habit, and inventing a backend
// table for four strings would be the wrong trade. Reads and writes are
// defensive because Safari private mode throws on access.
function readRecents(): string[] {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(RECENTS_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function writeRecents(list: string[]): void {
  try {
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, RECENTS_MAX)));
  } catch {
    /* storage unavailable — recents are a convenience, never a requirement */
  }
}

export interface AgentConsoleProps {
  extensionId: number;
  /** Screen-pop record for the live call, polled by the page. */
  call: CurrentCall | null;
  /**
   * Reports this console's OWN Softphone instance and its live registration
   * state upward whenever the state changes. Exists so a sibling (the
   * connectivity self-test, `ConnectionTest`) can REUSE this exact,
   * already-registered session instead of registering a second one under
   * the same static SIP secret — `pjsip_webrtc.conf`'s `max_contacts = 1` /
   * `remove_existing = yes` means a second login silently evicts the first,
   * unregistering the agent while their own console still shows "Ready".
   */
  onPhoneChange?: (phone: Softphone, state: PhoneState, seq: number) => void;
  /**
   * True while the connectivity self-test (`ConnectionTest`, via
   * AgentPanelPage) has its own echo test call up on THIS console's live
   * softphone. Swaps the call surface's "Unknown caller / No contact
   * record" for an unmistakable "Test call" label — an agent self-testing
   * their line must never read that as a dropped or misrouted real
   * customer. Never set for a real call; `call` (the screen-pop record)
   * drives the normal presentation exactly as before.
   */
  testCallActive?: boolean;
}

/**
 * The agent call console — presence, live-call surface and mid-call transfer in
 * a single card.
 *
 * Microphone permission is only ever requested from inside `goOnline` (the
 * agent's own click) — never on mount — since an unprompted mic prompt on a
 * dashboard reads as alarming and just gets denied.
 */
export function AgentConsole({ extensionId, call, onPhoneChange, testCallActive }: AgentConsoleProps) {
  const phoneRef = useRef<Softphone | null>(null);
  if (!phoneRef.current) phoneRef.current = new Softphone();
  /** The sound a call makes. Until this existed the console was silent and
   *  503 rings went unanswered at exactly the 20s member timeout. */
  const alertRef = useRef<AlertTone | null>(null);
  if (!alertRef.current) alertRef.current = new AlertTone();
  const audioRef = useRef<HTMLAudioElement>(null);
  // Whether the agent has asked to be online, independent of whether the
  // softphone currently IS. Survives a dropped transport, which is exactly
  // what tells the recovery effect below that a reconnect is wanted.
  const wantsOnlineRef = useRef(false);
  // Guards against a second recovery starting while one is still in flight —
  // visibilitychange and focus routinely fire together.
  const recoveringRef = useRef(false);
  // Flipped false on unmount so a still-in-flight goOnline() chain (mic prompt
  // / login / credential fetch / connect) can no-op instead of re-logging the
  // agent in or opening a SIP registration after the operator has already
  // ended their shift — see the effect below.
  const mountedRef = useRef(true);

  const [phoneState, setPhoneState] = useState<PhoneState>("offline");
  // beads F4: bumped every time onState fires (below), independent of
  // whether the new PhoneState string differs from the last one. A same-
  // string transition (e.g. "on-call" -> "on-call", the self-test's stale-
  // busy self-heal landing on the same word the stale value already held)
  // is otherwise invisible to a useEffect keyed on `phoneState` alone —
  // React's own same-value setState bailout skips re-running it. Threaded
  // alongside `phoneState` wherever downstream consumers need to observe
  // every real transition, not just every value CHANGE.
  const [stateSeq, setStateSeq] = useState(0);
  const [connecting, setConnecting] = useState(false);
  const [paused, setPaused] = useState(false);
  /** Set when the SERVER signed this seat out — the reason, for the banner. */
  const [signedOutBy, setSignedOutBy] = useState<string | null>(null);
  /** Asterisk cannot confirm it can ring this seat, whatever our SIP state says. */
  const [unreachable, setUnreachable] = useState(false);
  /** A server-side sign-out that arrived while a call was up.
   *
   *  Acting on it immediately would tear down the softphone, and teardown
   *  BYEs the live session — so the customer gets hung up on mid-sentence
   *  because a background rule decided the seat was idle. The sign-out is
   *  held here until the call ends. The seat is already unreachable
   *  server-side during the wait, so no new call can land on it. */
  const pendingSignOutRef = useRef<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The call is up and audio is arriving, but this device is not playing it —
  // the agent hears silence while the far end hears them normally. Kept apart
  // from `error`: nothing is wrong with the platform, and the fix is a tap the
  // agent has to make, so this renders as an action rather than a complaint.
  const [audioBlocked, setAudioBlocked] = useState(false);

  const [held, setHeld] = useState(false);
  const [holding, setHolding] = useState(false);
  const [holdError, setHoldError] = useState<string | null>(null);

  const [transferOpen, setTransferOpen] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [transferTo, setTransferTo] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [recents, setRecents] = useState<string[]>(() => readRecents());

  // Manual dialling. `dialing` covers the gap between the agent's press and the
  // INVITE producing a state change, which on a slow network is long enough to
  // press Call twice.
  const [dialing, setDialing] = useState(false);
  const [keypadOpen, setKeypadOpen] = useState(false);

  const registered = phoneState !== "offline";
  // Read from event listeners that must NOT be re-bound on every state change
  // (see the recovery effect below), so they always see the current values
  // rather than whatever was current when they were attached.
  const registeredRef = useRef(registered);
  registeredRef.current = registered;
  const onCall = phoneState === "on-call";
  const ringing = phoneState === "ringing";
  // An outbound call of ours is in flight. Lasts from the INVITE until the far
  // end answers, refuses, or the agent gives up — which is the entire window
  // the key pad needs to show "Calling…" for.
  const calling = phoneState === "calling";

  const goOnline = useCallback(async (opts?: { skipMicPrompt?: boolean }) => {
    setError(null);
    // The agent's INTENT to be online, which outlives any single transport.
    // Only this — never the current phone state — authorises the automatic
    // recovery below to re-register them.
    wantsOnlineRef.current = true;

    // beads F2: a registration blip during a REAL, still-live call
    // (Softphone.hasActiveSession() — the RTP media is independent of
    // whatever signaling event just dropped the registration) is not a dead
    // session to rebuild. connect() unconditionally tears down whatever
    // SimpleUser instance exists first (Softphone.teardown()), which would
    // hang up on the customer purely because the console's PHONE STATE said
    // "offline" while the call itself never ended. Re-registering in place
    // is the correct, and only safe, recovery here — no mic prompt, no
    // agentLogin, no fresh credentials; the softphone is already up.
    if (phoneRef.current?.hasActiveSession()) {
      setConnecting(true);
      try {
        await phoneRef.current.verifyRegistration();
      } finally {
        if (mountedRef.current) setConnecting(false);
      }
      return;
    }

    setConnecting(true);
    try {
      // Skipped by the automatic recovery path only: that path can only run
      // for a console that was ALREADY online, which means this prompt has
      // already been answered once for this page. Re-requesting it from a
      // handler with no user gesture behind it (a tab simply becoming
      // visible again) is what mobile browsers reject outright, which would
      // turn every recovery into a spurious "microphone blocked" error.
      if (!opts?.skipMicPrompt) {
        // Unlock the speaker while the tap that got us here is still on the
        // stack. Everything below (mic prompt, login, credentials, REGISTER)
        // outlives the browser's transient-activation window, and by the time
        // a call is answered there is no gesture left to spend.
        void primeAudioPlayback(audioRef.current);
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch {
          if (mountedRef.current) setError(MIC_BLOCKED_ERROR);
          return;
        }
        // The operator may have ended their shift (unmounting this console) while
        // the mic permission prompt was still open — don't mark them available or
        // open a SIP registration nobody is left to answer.
        if (!mountedRef.current) return;
        // Only needed to trigger/confirm the permission prompt — the softphone
        // manages its own mic access per call via SIP.js.
        stream.getTracks().forEach((track) => track.stop());
      }

      try {
        await agentLogin(extensionId);
        if (!mountedRef.current) return; // ended shift mid-login
        const creds = await getSipCredentials(extensionId);
        if (!mountedRef.current) return; // ended shift mid-fetch

        const connectPromise = phoneRef.current!.connect(creds, audioRef.current!, {
          onState: (s) => {
            if (mountedRef.current) {
              setPhoneState(s);
              setStateSeq((n) => n + 1);
            }
          },
          onIncoming: () => {
            /* screen-pop already covers who's calling; nothing extra to show */
          },
          onError: (message) => {
            // Softphone's own errors (registration failure, dropped transport)
            // are all "we can't reach the phone server" from the operator's
            // point of view — the underlying reason isn't actionable for them,
            // so it's logged, not displayed.
            console.error("softphone error:", message);
            if (mountedRef.current) setError(PHONE_SERVER_ERROR);
          },
          // One call being refused is NOT a sick phone server, and must never
          // be reported as one: the message here already names what happened
          // ("That line is busy", "They declined the call") in terms the agent
          // can act on, so it goes through verbatim. Collapsing these into
          // PHONE_SERVER_ERROR told operators the platform was unreachable
          // every time a single number turned them down.
          onCallFailed: (message) => {
            if (mountedRef.current) setError(message);
          },
          onAudioBlocked: () => {
            if (mountedRef.current) setAudioBlocked(true);
          },
        });

        // beads F3: bounded, deliberately — see GO_ONLINE_TIMEOUT_MS's doc
        // comment. Without this race, a silently-dropped network leaves
        // `connect()` neither resolving nor rejecting, and the `finally`
        // below never runs, so the Online button is stuck on "Connecting…"
        // with no way out short of a page reload.
        const outcome = await Promise.race([
          connectPromise.then(() => "connected" as const),
          new Promise<"timeout">((resolve) =>
            setTimeout(() => resolve("timeout"), GO_ONLINE_TIMEOUT_MS),
          ),
        ]);
        if (outcome === "timeout") {
          if (mountedRef.current) setError(PHONE_SERVER_ERROR);
          // The abandoned connect() attempt may still be pending
          // indefinitely — disconnect() bumps Softphone's own generation
          // guard, making that attempt's eventual continuation (if it ever
          // settles) a no-op against this now-superseded instance, and
          // actually releases the stuck WebSocket/registration attempt
          // instead of leaving it running in the background.
          void phoneRef.current?.disconnect();
          // connect() fires onState('registering') SYNCHRONOUSLY before the
          // race is awaited, so without this the console stays 'registering'
          // (which reads as registered) behind a torn-down SimpleUser: wedged
          // on 'Connecting', Online disabled, heartbeat still holding the seat,
          // with no way out but a reload (beads jkgz). Reflect the teardown.
          if (mountedRef.current) {
            setPhoneState("offline");
            setPaused(false);
          }
        }
      } catch {
        if (mountedRef.current) setError(PHONE_SERVER_ERROR);
      }
    } finally {
      if (mountedRef.current) setConnecting(false);
    }
  }, [extensionId]);

  /**
   * Explicit "Offline" — the agent stays on the page but stops taking calls.
   *
   * Softphone.disconnect() detaches the SIP delegate before tearing down, so
   * no onServerDisconnect will arrive to move state for us: this has to set
   * "offline" itself, and clear the break flag so the next Online doesn't come
   * back up already paused.
   */
  const goOffline = useCallback(async () => {
    setError(null);
    // Deliberate: stop the automatic recovery from quietly putting them back
    // online the next time this tab is looked at.
    wantsOnlineRef.current = false;
    setTransferOpen(false);
    try {
      await phoneRef.current?.disconnect();
    } finally {
      if (mountedRef.current) {
        setPhoneState("offline");
        setPaused(false);
      }
    }
    agentLogout(extensionId).catch(() => {
      /* best-effort — the presence reaper's TTL is the backstop */
    });
  }, [extensionId]);

  const setPausedRemote = useCallback(
    async (next: boolean) => {
      try {
        await agentPause(extensionId, next);
        setPaused(next);
        setError(null);
      } catch {
        setError(PHONE_SERVER_ERROR);
      }
    },
    [extensionId],
  );

  const handleAnswer = useCallback(() => {
    // Chirp first, then answer 100ms later. The chirp marks the moment the
    // customer becomes live, so the agent does not talk over their first word
    // -- and answering after it means no bridge exists while it sounds, so
    // there is no path for the customer to hear it even in principle.
    alertRef.current?.playConnect();
    window.setTimeout(() => void phoneRef.current?.answer(), 100);
  }, []);

  const handleHangup = useCallback(() => {
    void phoneRef.current?.hangup();
  }, []);

  const toggleMute = useCallback(() => {
    const next = !muted;
    phoneRef.current?.mute(next);
    setMuted(next);
  }, [muted]);

  /**
   * The warm-transfer session, read through the SAME react-query key
   * WarmTransferPanel uses — one poll, two readers, no prop drilling and no
   * chance of the call bar and the speed-dial list disagreeing about which
   * stage the transfer is at.
   *
   * The bar needs this because the flow the operator signed off on
   * ("Conference & Merge Flow", six states) puts Conference, Merge and Back
   * to speed dial IN THE BAR, changing with the stage, while the panel below
   * carries the speed-dial list and the running commentary.
   */
  const { data: warm } = useQuery({
    queryKey: ["warm-transfer"],
    queryFn: () => getWarmTransfer(extensionId),
    enabled: onCall,
    refetchInterval: onCall ? WARM_POLL_MS : false,
  });

  const qc = useQueryClient();
  const refreshWarm = useCallback(
    () => qc.invalidateQueries({ queryKey: ["warm-transfer"] }),
    [qc],
  );

  // A buyer who hung up mid-conference comes back as `consulting` with no live
  // buyer — the same reconciliation WarmTransferPanel does. Treated as "no
  // transfer running" so the bar offers Conference again rather than a Merge
  // that would 409.
  const buyerGone =
    warm?.state === "consulting" && !warm.buyer_up && warm.message === "buyer_no_answer";
  const warmLive = warm && warm.state !== "ended" && !buyerGone ? warm : null;
  const consulting = warmLive?.state === "consulting";
  const merged = warmLive?.state === "conference" || warmLive?.state === "agent_left";
  const buyerUp = Boolean(warmLive?.buyer_up);

  const merge = useMutation({
    mutationFn: () => mergeWarmTransfer(extensionId),
    onSuccess: () => void refreshWarm(),
  });
  const backToSpeedDial = useMutation({
    mutationFn: () => cancelWarmTransfer(extensionId),
    onSuccess: () => {
      // Land the agent back on the list, per state 3 -> 4 of the flow: the
      // attempt is dropped, the client's hold is untouched, and the speed dial
      // reopens so they can try someone else.
      setTransferOpen(true);
      void refreshWarm();
    },
  });
  const leave = useMutation({
    mutationFn: () => leaveWarmTransfer(extensionId),
    onSuccess: () => void refreshWarm(),
  });
  const warmBusy = merge.isPending || backToSpeedDial.isPending || leave.isPending;

  /**
   * The three mid-call disclosures are mutually exclusive: opening one closes
   * the others.
   *
   * Not a tidiness preference. Transfer's buyer buttons and Consent's template
   * buttons are both rows of small pills, and one of them DIALS A THIRD PARTY
   * while the other sends a text. Production hit exactly that: "when you hit
   * consent it initiates another call" — the transfer destinations were on
   * screen the whole call, immediately above the consent panel, and the agent
   * reached for the wrong row. One panel at a time means the pills on screen
   * always belong to the thing the agent just asked for.
   */
  const openPanel = useCallback((panel: "keypad" | "transfer" | "consent") => {
    setKeypadOpen((open) => (panel === "keypad" ? !open : false));
    setTransferOpen((open) => (panel === "transfer" ? !open : false));
    setConsentOpen((open) => (panel === "consent" ? !open : false));
    // The transfer panel's own error belongs to the attempt that produced it,
    // not to the next time the agent opens something.
    if (panel !== "transfer") setTransferError(null);
  }, []);

  /**
   * Hold is a re-INVITE, not a local toggle like mute — it can be refused,
   * and it takes a network round trip. So the button is disabled while it is
   * in flight (sip.js rejects a second hold request that overlaps the first),
   * and the resulting state is read back from the softphone rather than
   * assumed: if the re-INVITE failed, isHeld() still reports the truth and
   * the label stays honest instead of claiming the customer is hearing music
   * they aren't.
   */
  const toggleHold = useCallback(async () => {
    const phone = phoneRef.current;
    if (!phone || holding) return;
    const next = !held;
    setHoldError(null);
    setHolding(true);
    try {
      await phone.hold(next);
    } catch (err) {
      if (!mountedRef.current) return;
      setHoldError(err instanceof Error ? err.message : String(err));
    } finally {
      if (mountedRef.current) {
        setHeld(phone.isHeld());
        setHolding(false);
      }
    }
  }, [held, holding]);

  const submitTransfer = useCallback(
    async (target: string) => {
      const trimmed = target.trim();
      if (!trimmed) {
        setTransferError(TRANSFER_INVALID_TARGET);
        return;
      }
      setTransferError(null);
      setTransferring(true);
      try {
        await phoneRef.current!.transfer(trimmed);
        if (!mountedRef.current) return;
        // Only remember targets that actually got a REFER accepted — a typo
        // the far end rejected has no business in the quick-pick list.
        setRecents((prev) => {
          const next = [trimmed, ...prev.filter((r) => r !== trimmed)].slice(0, RECENTS_MAX);
          writeRecents(next);
          return next;
        });
        setTransferOpen(false);
        setTransferTo("");
      } catch (err) {
        if (!mountedRef.current) return;
        const message = err instanceof Error ? err.message : "";
        // Only the two agent-actionable failures are echoed verbatim; a REFER
        // the far end refused isn't something they can fix mid-call.
        if (message === TRANSFER_INVALID_TARGET || message === TRANSFER_NO_CALL) {
          setTransferError(message);
        } else {
          console.error("transfer failed:", err);
          setTransferError(TRANSFER_FAILED_ERROR);
        }
      } finally {
        if (mountedRef.current) setTransferring(false);
      }
    },
    [],
  );

  /**
   * Place a manual call from the key pad.
   *
   * Everything that decides whether this call may happen at all — the seat's
   * caller ID, the trunk, the account's credit — lives in the dialplan, keyed
   * on the authenticated SIP endpoint. This side sends a number and nothing
   * else; `dialInfo` below only decides whether to *offer* the key pad.
   */
  const submitDial = useCallback(async (target: string) => {
    setError(null);
    setDialing(true);
    try {
      await phoneRef.current!.call(target);
    } catch (err) {
      if (!mountedRef.current) return;
      const message = err instanceof Error ? err.message : "";
      if (message === CALL_INVALID_TARGET || message === CALL_NOT_CONNECTED) {
        setError(message);
      } else {
        console.error("dial failed:", err);
        setError(DIAL_FAILED_ERROR);
      }
    } finally {
      if (mountedRef.current) setDialing(false);
    }
  }, []);

  const sendTone = useCallback((tone: string) => {
    phoneRef.current?.sendDtmf(tone);
  }, []);

  // Whether this seat may dial out by hand, and as what number. Fetched only
  // once the agent is actually registered — an offline console has no key pad
  // to gate. Failure is silent and treated as "no key pad": an agent who can't
  // be told which number they'd be calling from shouldn't be dialling
  // strangers from an unknown line.
  const { data: dialInfo } = useQuery({
    queryKey: ["agent", "dial-info", extensionId],
    queryFn: () => getDialInfo(extensionId),
    enabled: registered,
    retry: false,
    staleTime: 300_000,
  });

  // Quick-pick transfer targets, fetched only once the agent opens the panel —
  // the console must not fire an admin-scoped request just to sit idle.
  // `/extensions/` 403s for non-admin operators, so a failure here is silently
  // tolerated: the panel still has recents and free-form entry.
  const { data: extensionPage } = useQuery({
    queryKey: ["extensions", "transfer-targets"],
    queryFn: () => listExtensions({ per_page: 100, active_only: true }),
    enabled: transferOpen,
    retry: false,
    staleTime: 300_000,
  });

  const quickPicks = useMemo(() => {
    const forwards = (extensionPage?.data ?? [])
      .map((e) => e.forward_to?.trim())
      .filter((v): v is string => Boolean(v));
    return Array.from(new Set([...recents, ...forwards])).slice(0, 6);
  }, [extensionPage, recents]);

  // Report this console's own Softphone instance + live state upward on every
  // change — see the `onPhoneChange` prop doc comment. Deliberately does NOT
  // depend on `onPhoneChange` itself: a parent that doesn't memoize its
  // callback would otherwise re-fire this on every one of ITS renders, not
  // just on a real phoneState change. Reporting the same (state, instance)
  // pair twice is harmless (the parent's own setState bails out on an equal
  // value), so reading the latest callback via a ref is enough.
  const onPhoneChangeRef = useRef(onPhoneChange);
  onPhoneChangeRef.current = onPhoneChange;
  useEffect(() => {
    onPhoneChangeRef.current?.(phoneRef.current!, phoneState, stateSeq);
  }, [phoneState, stateSeq]);

  // beads F1 — the call guard AuthContext consults before ever ending an
  // agent's session mid-call (see callGuard.ts's own doc comment). Mirrors
  // Softphone.hasActiveSession() — the actual source of truth for "is there
  // a live call" — rather than the phoneState string, which is exactly the
  // value that can go stale (beads_php_master_phone_system-0uf). Keyed on
  // [stateSeq] (not just [phoneState]) for the same reason the propagation
  // effect above is: a same-string onState transition must still be
  // observed here.
  useEffect(() => {
    setCallGuardActive(phoneRef.current?.hasActiveSession() ?? false);
  }, [phoneState, stateSeq]);

  // Belt-and-suspenders: whatever the reason this console unmounts, it must
  // never leave the call guard stuck reporting a live call that no longer
  // has a mounted console behind it.
  useEffect(() => {
    return () => setCallGuardActive(false);
  }, []);

  // Live call duration. Anchored to a wall-clock start rather than incremented
  // per tick so a throttled background tab can't make the timer drift behind
  // the real call.
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!onCall) {
      setSeconds(0);
      return;
    }
    const startedAt = Date.now();
    setSeconds(0);
    const id = setInterval(() => setSeconds(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(id);
  }, [onCall]);

  // A call ending (or the phone dropping) while muted must not leave the button
  // stuck on "Unmute" for the next call — reset both our own state and the
  // underlying session so a fresh call always starts unmuted. The transfer
  // panel closes with it: it has no meaning without a live call.
  useEffect(() => {
    if (onCall) return;
    setMuted((wasMuted) => {
      if (wasMuted) phoneRef.current?.mute(false);
      return false;
    });
    setTransferOpen(false);
    setTransferError(null);
    setKeypadOpen(false);
    // Hold rides along for the same reason as mute: the session that was held
    // is gone, so there is nothing left to un-hold. Unlike mute there is no
    // underlying flag to clear — the re-INVITE state died with the session —
    // so this is purely resetting our own view of it.
    setHeld(false);
    setHolding(false);
    setHoldError(null);
    setConsentOpen(false);
    // Same reasoning for the silent-speaker prompt: it is about one call, and
    // leaving it up after that call has ended tells the agent their audio is
    // broken when there is nothing playing at all.
    setAudioBlocked(false);
  }, [onCall, phoneState]);

  // Tracks whether this component is still mounted, for goOnline()'s
  // post-await checks above.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Heartbeat every 30s while registered — stops as soon as the phone drops
  // back to offline (deliberate logout, or a dropped transport).
  useEffect(() => {
    if (!registered) return;
    const id = setInterval(() => {
      agentHeartbeat(extensionId)
        .then((r) => {
          // The server signed this seat out from underneath us — the idle
          // sweep, or a manager on the floor board. Until 2026-09-21 this
          // reply was discarded, so the console kept showing "Ready" while no
          // call could ever arrive: the agent does not think they were signed
          // out, they think the queue has forgotten them.
          if (!mountedRef.current) return;
          if (r?.signed_out) {
            const why = r.signed_out_reason ?? "system";
            if (phoneRef.current?.hasActiveSession()) {
              // Hold it. Deliberately does NOT set signedOutBy: that opens a
              // full-screen modal, and throwing one over a rep mid-call is its
              // own way of losing the call. Mirrors the deferral AuthContext
              // already does for an expired token.
              pendingSignOutRef.current = why;
              return;
            }
            setSignedOutBy(why);
            void goOffline();
            return;
          }
          // Not signed out, but the switch cannot reach us. The console would
          // otherwise go on showing "Ready" while no call can arrive -- the
          // exact shape of "logged in on their end, not online in the manager
          // view". Surfaced, not auto-fixed: signing them out would take a
          // working rep off the floor over what is often a transient qualify
          // failure, and a re-registration is theirs to trigger.
          setUnreachable(Boolean(r?.unreachable));
        })
        .catch(() => {
          // best-effort — a missed beat just shortens the presence TTL, the
          // reaper backstop handles a truly dead session
        });
    }, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [registered, extensionId]);

  // Make the "Ready" pill honest about the SIP registration it claims,
  // specifically for a backgrounded mobile tab. SIP.js schedules its own
  // periodic REGISTER refresh on an ordinary JS timer, which mobile browsers
  // throttle or pause outright while a tab is hidden — a scheduled refresh
  // can simply never fire, silently letting the AOR contact expire on
  // Asterisk's side while this console keeps showing "Ready" the entire
  // time it was backgrounded (live evidence, beads_php_master_phone_system-0uf:
  // `pjsip show aor` reported zero contacts for a seat whose console had
  // never left "Ready"). Forcing a fresh REGISTER the moment the tab is
  // looked at again surfaces a genuinely lost registration — through the
  // SAME onUnregistered/offline path as any other rejected REGISTER,
  // dropping the console visibly out of Ready — within moments, instead of
  // it staying silently wrong indefinitely.
  // ...and, when it has already been lost, actually get it BACK.
  //
  // Two distinct failures share one trigger. (1) The refresh REGISTER never
  // fires while hidden, so the contact expires server-side while this console
  // still says "Ready" — verifyRegistration() is the answer to that one.
  // (2) The transport itself is suspended (iOS Safari backgrounding the tab
  // or the screen locking, a Wi-Fi/cellular handover). SIP.js reports that via
  // onServerDisconnect and the console correctly drops to "offline" — but
  // nothing brought it back: SimpleUser is built with no reconnection options,
  // and verifyRegistration() only re-sends a REGISTER over the very transport
  // that just died, which cannot rebuild it. Only goOnline() can, because
  // Softphone.connect() tears the old SimpleUser down and opens a fresh
  // WebSocket.
  //
  // This listener is deliberately NOT gated on `registered` any more. Gating
  // it there made it self-defeating: its entire purpose was to notice a dead
  // registration, but the instant it succeeded the state went offline,
  // `registered` flipped false, and the effect unmounted its own listener —
  // so the one mechanism that could have recovered the phone removed itself
  // at exactly the moment it became necessary. An agent whose screen had
  // simply locked came back to a phone that was dead until they reloaded the
  // page, and (because an offline console sends the self-test down its
  // temporary-softphone branch) to a "Test my connection" that failed too.
  //
  // Bound once, for the component's lifetime: `goOnline` is a stable
  // useCallback and the live state is read through refs, so this never
  // re-subscribes mid-session.
  useEffect(() => {
    const attempt = () => {
      if (document.visibilityState !== "visible") return;
      const phone = phoneRef.current;
      // Never re-registers an agent who deliberately went Offline, and never
      // runs before they have gone online even once.
      if (!phone || !wantsOnlineRef.current || recoveringRef.current) return;
      if (registeredRef.current) {
        void phone.verifyRegistration();
        return;
      }
      recoveringRef.current = true;
      void goOnline({ skipMicPrompt: true }).finally(() => {
        recoveringRef.current = false;
      });
    };
    document.addEventListener("visibilitychange", attempt);
    // `focus` and `online` catch the cases visibilitychange misses: a desktop
    // tab that was never hidden but whose machine slept, and a network that
    // came back without the page's visibility ever changing.
    window.addEventListener("focus", attempt);
    window.addEventListener("online", attempt);
    return () => {
      document.removeEventListener("visibilitychange", attempt);
      window.removeEventListener("focus", attempt);
      window.removeEventListener("online", attempt);
    };
  }, [goOnline]);

  // Auto-recover a registration that dropped while the agent was WATCHING.
  //
  // The visibility/focus/online listener above covers a console that was
  // backgrounded or whose network changed. It does not cover the case an agent
  // actually reported: console open, tab focused, the WebSocket dies underneath
  // (`res_http_websocket: Web socket closed abruptly`), and none of those three
  // events ever fires. `onServerDisconnect` moves state to offline and reports
  // the error, but nothing puts the phone back — so the queue rings a seat that
  // cannot answer. From the agent's chair that is "I got a call, no ring, only a
  // prompt, and it went away".
  //
  // Backs off rather than hammering: a server that is down should not get a
  // REGISTER storm from every open console. Stops entirely once registered, and
  // never runs for an agent who chose to be Offline or who is on a live call
  // (goOnline tears the SimpleUser down, which would drop that call).
  const recoverAttemptsRef = useRef(0);
  useEffect(() => {
    if (registered) {
      recoverAttemptsRef.current = 0;
      return;
    }
    if (!wantsOnlineRef.current || recoveringRef.current) return;
    if (phoneRef.current?.hasActiveSession?.()) return;

    const attempt = recoverAttemptsRef.current;
    // 2s, 4s, 8s, 16s, then every 30s for as long as it stays down.
    const delay = Math.min(2000 * 2 ** attempt, 30_000);
    const timer = window.setTimeout(() => {
      if (!wantsOnlineRef.current || recoveringRef.current) return;
      recoverAttemptsRef.current = attempt + 1;
      recoveringRef.current = true;
      void goOnline({ skipMicPrompt: true }).finally(() => {
        recoveringRef.current = false;
      });
    }, delay);
    return () => window.clearTimeout(timer);
  }, [registered, phoneState, goOnline]);

  // Best-effort logout on tab close/refresh — the 90s Redis-TTL presence
  // reaper is the backstop if this never lands.
  useEffect(() => {
    const handler = () => logoutBeacon(extensionId);
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [extensionId]);

  // Tear down the SIP registration and presence login if this console unmounts
  // (e.g. "End shift" within the SPA — a route-level unmount, not a tab close,
  // so `beforeunload` never fires for it). Best-effort: the presence reaper's
  // TTL is the backstop either way.
  useEffect(() => {
    return () => {
      void phoneRef.current?.disconnect();
      agentLogout(extensionId).catch(() => {
        /* best-effort — see comment above */
      });
    };
  }, [extensionId]);

  // Ring while the phone rings; stop the moment it does not.
  //
  // Keyed on `stateSeq` as well as `phoneState`: a same-string transition is
  // invisible to an effect keyed on the string alone, and a tone that fails to
  // stop is worse than one that fails to start.
  useEffect(() => {
    const tone = alertRef.current;
    if (!tone) return;
    if (phoneState === "ringing") {
      tone.startRing();
      return () => tone.stopRing();
    }
    tone.stopRing();
  }, [phoneState, stateSeq]);

  // iOS suspends the audio context when the tab goes to the background.
  useEffect(() => {
    const onVis = () => alertRef.current?.resumeIfSuspended();
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Release the audio device with the component.
  useEffect(() => {
    const tone = alertRef.current;
    return () => tone?.close();
  }, []);

  // A sign-out held during a call applies as soon as the call is down.
  useEffect(() => {
    const why = pendingSignOutRef.current;
    if (!why) return;
    if (phoneRef.current?.hasActiveSession()) return;
    pendingSignOutRef.current = null;
    setSignedOutBy(why);
    void goOffline();
    // `stateSeq` alongside `phoneState`: this must run on every real phone
    // transition, not just every value CHANGE. A same-string transition is
    // invisible to an effect keyed on the string alone (React's same-value
    // setState bailout), and the transition that releases a held sign-out is
    // exactly the kind that can repeat a word.
  }, [onCall, phoneState, stateSeq, goOffline]);

  // The modal is dismissed by the rep (either button), never by phone state.
  //
  // This used to clear itself whenever `registered` was true, which quietly
  // ate the notice in the case that matters most: a sign-out deferred during a
  // call applies the moment the call ends, and at that instant the phone is
  // still registered (state "idle", not "offline"), so the modal was set and
  // wiped in the same tick and the rep saw nothing at all. Registration is not
  // consent — only a click is.

  // Gated on the SERVER's word alone, deliberately not on local `registered`.
  // The console's own state is precisely what is wrong in this situation --
  // it still believes the seat is online, which is why the agent is sitting
  // there waiting. Waiting for the local teardown to finish before warning
  // them would reintroduce the silence this exists to break.
  const signedOutModal = signedOutBy ? (
    // A MODAL, not a banner. A rep who has been signed out and does not
    // realise it sits waiting for calls that cannot arrive -- which looks
    // exactly like the queue having forgotten them, and is the failure this
    // whole surface exists to prevent. A banner can be scrolled past or read
    // as decoration; this cannot be ignored or missed.
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="signed-out-title"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <span aria-hidden className="text-2xl">&#9888;&#65039;</span>
          <div>
            <h2 id="signed-out-title" className="text-lg font-semibold text-gray-900">
              You have been signed out
            </h2>
            <p className="mt-2 text-sm text-gray-700">
              {signedOutBy === "manager"
                ? "A manager signed this seat out."
                : signedOutBy === "unanswered"
                  ? "Multiple customers' calls went unanswered and were abandoned, so you have been signed out."
                  : signedOutBy === "unreachable"
                    ? "The phone system could not reach your phone, so this seat was signed out."
                    : signedOutBy === "deactivated"
                      ? "This agent login has been deactivated."
                      : signedOutBy === "rebind"
                        ? "This seat was reassigned to a different agent."
                        : "This seat was signed out."}
            </p>
            {/* Only for the rule that actually has an escalation behind it.
                Showing it for a dropped connection or a manager action tells
                the rep they are in trouble for something that was not their
                doing -- and the default text used to do exactly that for every
                reason except "manager". */}
            {signedOutBy === "unanswered" && (
              <p className="mt-2 text-sm text-gray-700">
                Check your headset and connection before signing back in.
              </p>
            )}
            <p className="mt-3 text-sm font-semibold text-gray-900">
              You will not receive any calls until you sign back in.
            </p>
          </div>
        </div>
        <button
          type="button"
          autoFocus
          onClick={() => {
            setSignedOutBy(null);
            void goOnline();
          }}
          className="mt-5 w-full rounded-lg bg-brand px-4 py-3 text-sm font-semibold text-white hover:bg-brand-dark"
        >
          Sign back in
        </button>
        <button
          type="button"
          onClick={() => setSignedOutBy(null)}
          className="mt-2 w-full rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          Stay signed out
        </button>
      </div>
    </div>
  ) : null;

  const toneKey = connecting
    ? "connecting"
    : ringing
      ? "ringing"
      : onCall || calling
        ? "oncall"
        : !registered
          ? "offline"
          : paused
            ? "break"
            : "ready";
  const tone = TONE[toneKey];
  // Keyed on the tone, so the dwell timer restarts on every real state change
  // (ready -> ringing -> on call -> ready) and never on a re-render.
  const { clock, held: readyFor } = useStateClock(toneKey);

  /**
   * The status word, which during a warm transfer describes the TRANSFER
   * rather than the phone.
   *
   * Straight off the approved flow, whose whole point is that the agent can
   * read their situation from one word: "On hold" is not the same situation
   * as "Hold + ringing", and neither is "Merged". The underlying softphone
   * state is `on-call` throughout all of them, so left to itself the header
   * says "On call" while the customer is sitting in hold music and a buyer is
   * ringing — true of the SIP session, useless to the person on the headset.
   */
  const warmStatus = merged
    ? "Merged"
    : consulting
      ? buyerUp
        ? "Hold + connecting"
        : "Hold + ringing"
      : onCall && held
        ? "On hold"
        : null;

  const statusLabel = connecting
    ? STATE_LABEL.registering
    : warmStatus
      ? warmStatus
      : ringing || onCall || calling
        ? STATE_LABEL[phoneState]
        : paused && registered
          ? "On break"
          : STATE_LABEL[phoneState];

  const contactName = call && [call.first_name, call.last_name].filter(Boolean).join(" ");
  const campaign = call?.campaign_name
    || (call?.campaign_id != null ? `Campaign ${call.campaign_id}` : null);
  // What to say under the number when there is no name. Three different
  // situations used to collapse into one "No contact record", which the floor
  // read as the pop having failed (F12, 2026-09-18). It had not: the list
  // behind their campaign was uploaded with phone numbers only.
  const noNameLine = !call
    ? "No contact record"
    : call.spool_id == null
      ? "Not a campaign lead"
      : "No name on file — this list has phone numbers only";
  const leadFacts = call ? leadFactLines(call) : [];

  return (
    <>
    {signedOutModal}
    <section
      aria-label="Call console"
      className={`relative mb-4 overflow-hidden rounded-2xl transition-colors duration-300 ${
        onCall
          ? "bg-slate-900 text-white shadow-xl"
          : ringing
            ? "bg-white shadow-xl ring-2 ring-brand"
            : "bg-white shadow"
      }`}
    >
      {/* The state spine — readable from the corner of an eye. */}
      <span aria-hidden className={`absolute left-0 top-0 h-full w-1.5 ${tone.spine}`} />
      {/*
        The only thing on this page that makes sound.

        `playsInline` and the absence of `hidden` are both load-bearing on
        iOS: `hidden` is `display: none`, which WebKit treats as grounds to
        refuse playback, and a media element with neither attribute is a
        candidate for fullscreen takeover rather than inline playback. An
        `<audio>` without `controls` renders no box, so dropping `hidden`
        costs nothing visually.
      */}
      <audio ref={audioRef} autoPlay playsInline />

      {/* --- Header: state, extension, live timer --------------------------- */}
      <div
        className={`flex items-center justify-between gap-4 border-b pl-8 pr-6 py-3 ${
          onCall ? "border-white/10" : "border-gray-100"
        }`}
      >
        <div className="flex items-center gap-2.5" role="status" aria-live="polite">
          <span className="relative flex h-2.5 w-2.5">
            {(ringing || (registered && !paused && !onCall)) && (
              <span
                className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 motion-reduce:animate-none ${tone.dot}`}
              />
            )}
            <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${tone.dot}`} />
          </span>
          <span
            className={`text-sm font-semibold uppercase tracking-wide ${tone.label}`}
          >
            {statusLabel}
          </span>
        </div>
        <div className="flex items-center gap-4">
          {onCall && (
            <span className="font-mono text-lg tabular-nums text-emerald-300" aria-label="Call duration">
              {formatDuration(seconds)}
            </span>
          )}
          {/* Always on screen so any screenshot of this console carries the
              time it was taken. */}
          <span
            className={`font-mono text-xs tabular-nums ${onCall ? "text-white/70" : "text-gray-500"}`}
            aria-label="Current time"
            title="Current time — so a screenshot of this screen can be checked against the call log"
          >
            {clock}
          </span>
          <span className={`text-xs ${onCall ? "text-white/50" : "text-gray-400"}`}>
            Ext {extensionId}
          </span>
        </div>
      </div>

      {/* --- Call surface --------------------------------------------------- */}
      {ringing || onCall ? (
        <div className="pl-8 pr-6 py-7">
          <div className="text-center">
            <div
              className={`whitespace-nowrap font-mono text-3xl font-semibold tabular-nums tracking-tight sm:text-5xl ${
                onCall ? "text-white" : "text-gray-900"
              }`}
            >
              {testCallActive && !call ? "Test call" : call ? formatPhone(call.phone) : "Unknown caller"}
            </div>
            <div className={`mt-2 text-base ${onCall ? "text-white/70" : "text-gray-600"}`}>
              {testCallActive && !call ? (
                "Echo test — you'll hear yourself. Hang up when you're done."
              ) : (
                <>
                  {contactName || noNameLine}
                  {campaign && (
                    <>
                      <span className={onCall ? "text-white/30" : "text-gray-300"}> · </span>
                      {campaign}
                    </>
                  )}
                </>
              )}
            </div>
            {/* F6: the list had no name; a colleague wrote one down last
                time. Say so — it is a note, not the customer's own record. */}
            {call?.known_name && contactName && (
              <div className={`mt-1 text-xs ${onCall ? "text-white/50" : "text-gray-400"}`}>
                Name from a previous call
                {call.known_name.agent_name ? ` — ${call.known_name.agent_name}` : ""}
              </div>
            )}
          </div>

          {/* What the NUMBER says, for the (common) lead with no name behind
              it: where they are and what time it is there, the list and
              attempt, the line, and whether this account has spoken to them
              before. Every line is optional — nothing here is guessed. */}
          {call && (
            <div
              data-testid="lead-facts"
              className={`mx-auto mt-5 max-w-md rounded-xl px-4 py-3 text-left text-sm ${
                onCall ? "bg-white/5 text-white/90" : "bg-gray-50 text-gray-800"
              }`}
            >
              {call.dnc && (
                <p
                  className={`mb-2 rounded-lg px-3 py-2 text-sm font-semibold ${
                    onCall ? "bg-red-500/20 text-red-200" : "bg-red-50 text-red-800"
                  }`}
                >
                  On a do-not-call list
                  {call.dnc.listed_at ? ` since ${formatHistoryDate(call.dnc.listed_at)}` : ""}
                  {call.dnc.reason ? ` — ${call.dnc.reason}` : ""}
                </p>
              )}
              {leadFacts.length > 0 && (
                <ul className="space-y-0.5">
                  {leadFacts.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )}
              <p className={`mt-2 text-xs font-semibold uppercase tracking-wide ${onCall ? "text-white/50" : "text-gray-500"}`}>
                {call.prior_calls === 0
                  ? "First call to this number"
                  : `Called ${call.prior_calls} time${call.prior_calls === 1 ? "" : "s"} before`}
              </p>
              {call.history.length > 0 && (
                <ol className="mt-1 space-y-1">
                  {call.history.slice(0, 5).map((h) => (
                    <li key={`${h.at}-${h.campaign_id ?? "x"}`} className="flex flex-wrap gap-x-2">
                      <span className={onCall ? "text-white/60" : "text-gray-500"}>
                        {formatHistoryDate(h.at)}
                      </span>
                      <span className="font-medium">{h.outcome}</span>
                      {h.agent_name && <span>· {h.agent_name}</span>}
                      {h.billsec > 0 && <span>· {formatDuration(h.billsec)}</span>}
                      {h.campaign_name && (
                        <span className={onCall ? "text-white/50" : "text-gray-400"}>· {h.campaign_name}</span>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}

          {/* Screen-pop: per-lead custom fields from the upload (extra_data). */}
          {call && call.custom_fields && Object.keys(call.custom_fields).length > 0 && (
            <dl
              className={`mx-auto mt-5 grid max-w-md grid-cols-[minmax(0,auto)_1fr] gap-x-4 gap-y-1.5 rounded-xl px-4 py-3 text-left text-sm ${
                onCall ? "bg-white/5 text-white/90" : "bg-gray-50 text-gray-800"
              }`}
            >
              {Object.entries(call.custom_fields).map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className={`truncate font-medium ${onCall ? "text-white/50" : "text-gray-500"}`}>
                    {k}
                  </dt>
                  <dd className="min-w-0 break-words">{String(v)}</dd>
                </div>
              ))}
            </dl>
          )}

          {ringing ? (
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <button
                onClick={handleAnswer}
                className="flex-1 whitespace-nowrap rounded-xl bg-emerald-600 px-6 py-5 text-lg font-semibold text-white shadow-lg shadow-emerald-600/20 transition hover:bg-emerald-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-300"
              >
                Answer
              </button>
              <button
                onClick={handleHangup}
                className="rounded-xl bg-gray-100 px-6 py-5 text-base font-medium text-gray-700 transition hover:bg-gray-200 focus:outline-none focus-visible:ring-4 focus-visible:ring-gray-300 sm:w-40"
              >
                Decline
              </button>
            </div>
          ) : merged ? (
            /* State 6 of the flow — merged. "Mute, Keypad and Merge drop off
               the bar — nothing left to do but exit." Leaving is a different
               act from hanging up on someone: the two parties stay connected
               without the agent, so the endpoint is `leave`, not a hangup, and
               it gets the whole bar because it is the only thing left. */
            <div className="mt-7">
              <button
                onClick={() => leave.mutate()}
                disabled={warmBusy}
                className="w-full whitespace-nowrap rounded-xl bg-red-600 px-4 py-4 text-base font-semibold text-white transition hover:bg-red-500 focus:outline-none focus-visible:ring-4 focus-visible:ring-red-400/50 disabled:opacity-60"
              >
                {leave.isPending ? "Leaving…" : "Hang up & leave call"}
              </button>
            </div>
          ) : (
            <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-6">
              <button
                onClick={handleHangup}
                className="whitespace-nowrap rounded-xl bg-red-600 px-4 py-4 text-base font-semibold text-white transition hover:bg-red-500 focus:outline-none focus-visible:ring-4 focus-visible:ring-red-400/50"
              >
                Hang up
              </button>
              {/* Held is a state the customer can hear (music, not silence),
                  so it gets the same loud treatment as an active Unmute
                  rather than the quiet "panel is open" styling. */}
              <button
                onClick={() => void toggleHold()}
                aria-pressed={held}
                disabled={holding}
                className={`whitespace-nowrap rounded-xl px-4 py-4 text-base font-medium transition focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30 disabled:opacity-60 ${
                  held
                    ? "bg-sky-400 text-slate-900 hover:bg-sky-300"
                    : "bg-white/10 text-white hover:bg-white/20"
                }`}
              >
                {holding ? "…" : held ? "Resume" : "Hold"}
              </button>
              <button
                onClick={toggleMute}
                aria-pressed={muted}
                className={`whitespace-nowrap rounded-xl px-4 py-4 text-base font-medium transition focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30 ${
                  muted
                    ? "bg-amber-400 text-slate-900 hover:bg-amber-300"
                    : "bg-white/10 text-white hover:bg-white/20"
                }`}
              >
                {muted ? "Unmute" : "Mute"}
              </button>
              <button
                onClick={() => openPanel("keypad")}
                aria-expanded={keypadOpen}
                className={`whitespace-nowrap rounded-xl px-4 py-4 text-base font-medium text-white transition focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30 ${
                  keypadOpen ? "bg-white/20 ring-1 ring-white/50" : "bg-white/10 hover:bg-white/20"
                }`}
              >
                Keypad
              </button>
              {/* States 1 vs 2-5. Conference is the entry point — "it starts a
                  warm handoff instead of dropping the agent out immediately"
                  — and once the agent is inside the flow the same slot becomes
                  Merge, which stays greyed out until someone actually answers.
                  Never both: they are the same decision at two different
                  stages, and two live buttons here is what let an agent dial a
                  buyer when they meant to do something else. */}
              {!warmLive && !transferOpen ? (
                <button
                  onClick={() => openPanel("transfer")}
                  aria-expanded={transferOpen}
                  className="whitespace-nowrap rounded-xl bg-emerald-500 px-4 py-4 text-base font-semibold text-slate-900 transition hover:bg-emerald-400 focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-300/50"
                >
                  Conference
                </button>
              ) : (
                <button
                  onClick={() => merge.mutate()}
                  // Rendered disabled rather than withheld so the agent can see
                  // the next step exists and why it is not available yet. The
                  // API refuses it with a 409 regardless, so nothing here is
                  // load-bearing.
                  disabled={!buyerUp || warmBusy}
                  title={buyerUp ? undefined : "Greyed out until someone answers"}
                  className={`whitespace-nowrap rounded-xl px-4 py-4 text-base font-semibold transition focus:outline-none focus-visible:ring-4 focus-visible:ring-violet-300/50 disabled:cursor-not-allowed disabled:opacity-40 ${
                    buyerUp
                      ? "bg-violet-500 text-white hover:bg-violet-400"
                      : "bg-white/10 text-white"
                  }`}
                >
                  {merge.isPending ? "Merging…" : "Merge"}
                </button>
              )}
              {/* State 3. Drops THIS attempt only — the client's hold is
                  untouched — and reopens the speed dial so the agent can try
                  someone else. Only while a buyer leg exists to back out of. */}
              {consulting && (
                <button
                  onClick={() => backToSpeedDial.mutate()}
                  disabled={warmBusy}
                  className="whitespace-nowrap rounded-xl bg-amber-400 px-4 py-4 text-base font-semibold text-slate-900 transition hover:bg-amber-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-300/50 disabled:opacity-60"
                >
                  {backToSpeedDial.isPending ? "…" : "Back to speed dial"}
                </button>
              )}
              <button
                onClick={() => openPanel("consent")}
                aria-expanded={consentOpen}
                className={`whitespace-nowrap rounded-xl px-4 py-4 text-base font-medium text-white transition focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30 ${
                  consentOpen ? "bg-white/20 ring-1 ring-white/50" : "bg-white/10 hover:bg-white/20"
                }`}
              >
                Consent
              </button>
            </div>
          )}

          {/* Says what the CUSTOMER is experiencing, not what the button did.
              An agent who forgets a held call is the failure mode worth
              designing against, and "they're hearing hold music" is the fact
              that prompts them to come back. */}
          {onCall && held && (
            <p className="mt-4 rounded-lg bg-sky-400/10 px-3 py-2 text-center text-sm text-sky-200 ring-1 ring-sky-400/30">
              On hold — they're hearing hold music and can't hear you.
            </p>
          )}
          {onCall && holdError && (
            <p className="mt-4 rounded-lg bg-red-500/10 px-3 py-2 text-center text-sm text-red-200 ring-1 ring-red-400/30">
              {holdError}
            </p>
          )}

          {/* Mid-call key pad — tones only. Same component as the idle dialler
              so the two never drift apart on layout or keyboard handling. */}
          {onCall && keypadOpen && (
            <div className="mt-4 rounded-xl bg-white/5 p-4 ring-1 ring-white/10">
              <Dialpad variant="dtmf" onTone={sendTone} />
              <p className="mt-3 text-center text-xs text-white/40">
                Tones go to the person you're on with — for menus and extensions.
              </p>
            </div>
          )}

          {onCall && <WarmTransferPanel onCall={onCall} pickerOpen={transferOpen} extensionId={extensionId} />}

          {onCall && consentOpen && (
            <ConsentPanel spoolId={call?.spool_id ?? null} phone={call?.phone ?? null} />
          )}

          {/* Transfer stays inline inside the cockpit — a modal mid-call steals
              focus from the conversation and costs an extra dismissal click. */}
          {onCall && transferOpen && (
            <div className="mt-4 rounded-xl bg-white/5 p-4 ring-1 ring-white/10">
              {quickPicks.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {quickPicks.map((pick) => (
                    <button
                      key={pick}
                      disabled={transferring}
                      onClick={() => void submitTransfer(pick)}
                      className="rounded-lg bg-white/10 px-3 py-2 font-mono text-sm text-white transition hover:bg-white/20 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                    >
                      {formatPhone(pick)}
                    </button>
                  ))}
                </div>
              )}
              <form
                className="flex flex-col gap-2 sm:flex-row"
                onSubmit={(e) => {
                  e.preventDefault();
                  void submitTransfer(transferTo);
                }}
              >
                <label htmlFor="transfer-to" className="sr-only">
                  Transfer to
                </label>
                <input
                  id="transfer-to"
                  value={transferTo}
                  onChange={(e) => setTransferTo(e.target.value)}
                  placeholder="Number or extension"
                  autoComplete="off"
                  className="flex-1 rounded-lg border border-white/15 bg-slate-950/60 px-3 py-2.5 font-mono text-sm text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={transferring}
                  className="whitespace-nowrap rounded-lg bg-brand px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-dark disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                >
                  {transferring ? "Transferring…" : "Transfer call"}
                </button>
              </form>
              <p className="mt-2 text-xs text-white/40">
                The call leaves your line as soon as it transfers.
              </p>
              {transferError && (
                <p className="mt-2 text-sm text-red-300" role="alert">
                  {transferError}
                </p>
              )}
            </div>
          )}
        </div>
      ) : (
        /* --- Presence surface: the only thing on screen when idle --------- */
        <div className="pl-8 pr-6 py-6">
          <div
            role="group"
            aria-label="Availability"
            /* Two states, not three: Break was removed 2026-09-21. A seat on
               break still held its place in the rotation while answering
               nothing, and the queue has no way to tell that from a seat whose
               phone has died -- either way the caller pays the ring timeout.
               Stepping away now means signing out, which is honest about what
               the floor board shows. */
            className="grid grid-cols-2 gap-1 rounded-xl bg-gray-100 p-1"
          >
            <button
              onClick={() => (registered ? void setPausedRemote(false) : void goOnline())}
              disabled={connecting || (registered && !paused)}
              aria-pressed={registered && !paused}
              className={`rounded-lg px-4 py-3 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 disabled:cursor-default ${
                registered && !paused
                  ? "bg-emerald-600 text-white shadow-sm disabled:opacity-100"
                  : "text-gray-600 hover:bg-white disabled:opacity-40"
              }`}
            >
              {connecting ? "Connecting…" : "Online"}
            </button>
            <button
              onClick={() => void goOffline()}
              disabled={!registered || connecting}
              aria-pressed={!registered}
              className={`rounded-lg px-4 py-3 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:cursor-default ${
                !registered
                  ? "bg-slate-600 text-white shadow-sm disabled:opacity-100"
                  : "text-gray-600 hover:bg-white disabled:opacity-40"
              }`}
            >
              Offline
            </button>
          </div>
          <p className="mt-3 text-center text-sm text-gray-500">
            {!registered
              ? "Go online to start taking calls."
              : paused
                ? "You're on break — no calls will reach you."
                : "Waiting for the next call."}
          </p>
          {unreachable && registered && (
            <div
              role="status"
              className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            >
              <strong>The phone system cannot reach your phone.</strong> You
              look online here, but calls are not being sent to you. Go Offline
              and back Online to re-register — if it keeps happening, tell your
              manager.
            </div>
          )}
          {registered && !paused && !onCall && (
            <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-500">
              <label htmlFor="ringvol">Ring volume</label>
              <input
                id="ringvol"
                type="range"
                min={0.4}
                max={1}
                step={0.1}
                defaultValue={alertRef.current?.volume ?? 0.8}
                onChange={(e) => {
                  if (alertRef.current) {
                    alertRef.current.volume = parseFloat(e.target.value);
                  }
                }}
                className="w-24"
              />
              <button
                type="button"
                onClick={() => alertRef.current?.playConnect()}
                className="rounded border border-gray-300 px-2 py-0.5 hover:bg-gray-50"
              >
                Test
              </button>
            </div>
          )}
          {registered && alertRef.current?.state() === "suspended" && (
            /* Say it rather than let them find out on a missed call. */
            <p className="mt-2 text-center text-xs text-amber-700">
              Ring sound is blocked — go Offline and back Online to enable it.
            </p>
          )}
          {registered && !paused && !onCall && (
            /* The dwell time, in the same frame as the status. A screenshot
               showing READY is only useful next to how long it had been
               READY -- that is the number the manager board disagrees with. */
            <p className="mt-1 text-center text-xs tabular-nums text-gray-400">
              Ready for {readyFor} · {clock}
            </p>
          )}

          {/* The key pad follows the SIP registration, not availability: a seat
              on break still has a working line, and dialling out by hand is
              most of what "on break" is for. It stays hidden entirely while
              offline — there is nothing to dial with. */}
          {registered && dialInfo && (
            <Dialpad
              variant="dial"
              onCall={(number) => void submitDial(number)}
              canDial={dialInfo.can_dial}
              blockedReason={dialInfo.reason}
              callerId={dialInfo.callerid}
              // Both halves matter: `dialing` covers the moment between the
              // press and the INVITE going out, `calling` covers the far
              // longer wait for the far end to ring and answer. On its own,
              // `dialing` cleared as soon as the INVITE was sent — the button
              // flicked back to "Call" in milliseconds while nothing had
              // happened yet, which is most of what "the dial pad is broken"
              // looked like from the agent's chair.
              busy={dialing || calling}
            />
          )}
        </div>
      )}

      {error && (
        <div
          role="alert"
          className={`ml-8 mr-6 mb-4 rounded-lg px-3 py-2 text-sm ${
            onCall
              ? "bg-red-500/15 text-red-200 ring-1 ring-red-400/30"
              : "border border-red-200 bg-red-50 text-red-600"
          }`}
        >
          {error}
        </div>
      )}

      {/*
        The call is fine; this device just isn't playing it. Rendered as a
        button because the fix REQUIRES a user gesture — a browser that has
        refused playback will keep refusing it to any retry the page makes on
        its own, so the tap is not a confirmation step, it is the mechanism.
      */}
      {audioBlocked && (
        <div
          role="alert"
          className={`ml-8 mr-6 mb-4 rounded-lg px-3 py-2 text-sm ${
            onCall
              ? "bg-amber-500/15 text-amber-100 ring-1 ring-amber-400/30"
              : "border border-amber-200 bg-amber-50 text-amber-800"
          }`}
        >
          <p className="font-medium">You may not be able to hear this call</p>
          <p className="mt-0.5 opacity-90">
            They can still hear you. Tap to turn on call audio — and check your
            ringer/silent switch if it stays quiet.
          </p>
          <button
            type="button"
            onClick={() => {
              void phoneRef.current?.resumePlayback().then((playing) => {
                if (mountedRef.current && playing) setAudioBlocked(false);
              });
            }}
            className={`mt-2 rounded-md px-3 py-1.5 text-sm font-semibold ${
              onCall
                ? "bg-amber-400 text-slate-900 hover:bg-amber-300"
                : "bg-amber-600 text-white hover:bg-amber-700"
            }`}
          >
            Turn on call audio
          </button>
        </div>
      )}
    </section>
    </>
  );
}

/** A clock that ticks, and how long the current state has held.
 *
 * Exists so a screenshot dates itself. Reps were sending "READY" screenshots
 * to show they were available while the manager board showed them not taking a
 * call for seven minutes, and there was no way to line the two up: the image
 * carried no time. Now it carries both the wall clock and the dwell time, so a
 * screenshot can be checked against the logs instead of argued about.
 *
 * Seconds are shown deliberately -- minute resolution is not enough to match
 * against a call log where the thing in dispute lasted under a minute.
 */
function useStateClock(stateKey: string) {
  const [now, setNow] = useState(() => new Date());
  const sinceRef = useRef<number>(Date.now());

  // Restart the dwell timer whenever the state itself changes.
  useEffect(() => {
    sinceRef.current = Date.now();
  }, [stateKey]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const heldSeconds = Math.max(0, Math.floor((now.getTime() - sinceRef.current) / 1000));
  return {
    clock: now.toLocaleTimeString([], {
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    }),
    held: heldSeconds < 60
      ? `${heldSeconds}s`
      : `${Math.floor(heldSeconds / 60)}m ${String(heldSeconds % 60).padStart(2, "0")}s`,
  };
}
